/**
 * Feature Flags Service
 *
 * Gerencia feature flags:
 * - Verifica se flag está ativada para um usuário específico
 * - Suporta rollout percentual
 * - Suporta beta testers
 * - Busca valores do servidor (ou usa defaults locais)
 */

import { supabase } from '@/integrations/supabase/client';
import {
  FEATURE_FLAGS,
  FeatureFlagName,
  getFeatureFlagConfig
} from '@/config/features/flags';

/**
 * Valor de flag retornado pela API
 */
export interface FeatureFlagValue {
  flagName: FeatureFlagName;
  enabled: boolean;
  rolloutPercentage: number;
  betaTesters: string[];
  reason: string;
}

/**
 * Cache local de flags
 * TTL: 5 minutos
 */
const flagCache = new Map<string, { value: boolean; timestamp: number }>();
const CACHE_TTL = 1000 * 60 * 5; // 5 minutos

/**
 * Serviço de feature flags
 */
export const featureFlagsService = {
  /**
   * Checar se uma flag está ativada para um usuário específico
   *
   * @param flagName - Nome da flag
   * @param userId - ID do usuário
   * @param userEmail - Email do usuário
   * @returns boolean - Se a flag está ativada
   */
  async checkFeatureFlag(
    flagName: FeatureFlagName,
    userId: string,
    userEmail: string
  ): Promise<boolean> {
    try {
      // 1. Verificar cache
      const cacheKey = `${flagName}:${userId}`;
      const cached = flagCache.get(cacheKey);
      if (cached && Date.now() - cached.timestamp < CACHE_TTL) {
        return cached.value;
      }

      // 2. Buscar do servidor
      const value = await this.fetchFeatureFlagFromServer(
        flagName,
        userId,
        userEmail
      );

      // 3. Guardar em cache
      flagCache.set(cacheKey, {
        value,
        timestamp: Date.now()
      });

      return value;
    } catch (error) {
      console.error(
        `Erro ao checar feature flag ${flagName} para ${userEmail}:`,
        error
      );

      // Em caso de erro, usar valor default (conservador)
      const config = getFeatureFlagConfig(flagName);
      return config.enabled && config.rolloutPercentage > 0;
    }
  },

  /**
   * Buscar valor de flag do servidor
   */
  async fetchFeatureFlagFromServer(
    flagName: FeatureFlagName,
    userId: string,
    userEmail: string
  ): Promise<boolean> {
    try {
      // Tentar buscar da tabela de feature_flags (se existir)
      // Por enquanto, usar configuração local
      const config = getFeatureFlagConfig(flagName);

      console.log(`[FeatureFlag] Checking ${flagName}:`, {
        userId,
        userEmail,
        enabled: config.enabled,
        rolloutPercentage: config.rolloutPercentage
      });

      if (!config.enabled) {
        console.log(`[FeatureFlag] ${flagName} is disabled globally`);
        return false;
      }

      // Verificar se é beta tester
      if (config.betaTesters?.includes(userEmail)) {
        console.log(`[FeatureFlag] ${userEmail} is beta tester for ${flagName}`);
        return true;
      }

      // Verificar rollout percentual
      const userHash = this.hashUserId(userId);
      const threshold = (config.rolloutPercentage / 100) * 0xffffffff;
      const result = userHash < threshold;

      console.log(`[FeatureFlag] ${flagName} rollout check:`, {
        userHash,
        threshold,
        rolloutPercentage: config.rolloutPercentage,
        result
      });

      return result;
    } catch (error) {
      console.error(`Erro ao buscar flag do servidor:`, error);
      return false;
    }
  },

  /**
   * Obter todas as flags e seus valores para um usuário
   */
  async getAllFeatureFlags(userId: string, userEmail: string): Promise<Record<FeatureFlagName, boolean>> {
    const result: Record<FeatureFlagName, boolean> = {} as any;

    const flagNames = Object.keys(FEATURE_FLAGS) as FeatureFlagName[];

    for (const flagName of flagNames) {
      result[flagName] = await this.checkFeatureFlag(flagName, userId, userEmail);
    }

    return result;
  },

  /**
   * Limpar cache de flags
   * Útil após atualizar flags no admin
   */
  clearCache(): void {
    flagCache.clear();
  },

  /**
   * Limpar cache de um usuário específico
   */
  clearUserCache(userId: string): void {
    Array.from(flagCache.keys()).forEach(key => {
      if (key.includes(userId)) {
        flagCache.delete(key);
      }
    });
  },

  /**
   * Hash simples de user ID para rollout percentual
   * Garante determinístico (mesmo usuário sempre na mesma faixa)
   */
  hashUserId(userId: string): number {
    let hash = 0;
    for (let i = 0; i < userId.length; i++) {
      const char = userId.charCodeAt(i);
      hash = ((hash << 5) - hash) + char;
      hash = hash & 0xffffffff;
    }
    return Math.abs(hash);
  }
}

/**
 * Serviço administrativo para gerenciar feature flags
 * (Apenas para /admin/feature-flags)
 */
export const featureFlagsAdminService = {
  /**
   * Buscar todas as flags (para admin panel)
   */
  async getAllFlags() {
    return Object.entries(FEATURE_FLAGS).map(([name, config]) => ({
      name,
      ...config
    }));
  },

  /**
   * Atualizar flag (apenas em admin)
   *
   * @param flagName - Nome da flag
   * @param updates - Atualizações
   * @returns Flag atualizada
   *
   * @example
   * ```ts
   * await featureFlagsAdminService.updateFlag('funil.timeline.view', {
   *   rolloutPercentage: 25,
   *   betaTesters: ['user@example.com']
   * });
   * ```
   */
  async updateFlag(
    flagName: FeatureFlagName,
    updates: Partial<typeof FEATURE_FLAGS[typeof flagName]>
  ) {
    // TODO: Implementar persistência em Supabase quando tabela existir
    // Por enquanto, retorna o que seria atualizado
    const current = getFeatureFlagConfig(flagName);
    const updated = { ...current, ...updates };

    // Limpar cache para que mudanças sejam refletidas
    featureFlagsService.clearCache();

    // Log para auditoria
    console.log(`[Feature Flag Updated] ${flagName}`, {
      before: current,
      after: updated,
      timestamp: new Date().toISOString()
    });

    return updated;
  },

  /**
   * Rollout automático de flag
   * Aumenta gradualmente a porcentagem
   *
   * @param flagName - Nome da flag
   * @param targetPercentage - Porcentagem alvo
   * @param duration - Duração em ms (aumenta gradualmente)
   */
  async rolloutFlag(
    flagName: FeatureFlagName,
    targetPercentage: number,
    duration: number = 3600000 // 1 hora default
  ) {
    const config = getFeatureFlagConfig(flagName);
    const current = config.rolloutPercentage;
    const step = targetPercentage - current;
    const interval = duration / Math.abs(step);

    // Criar job que aumenta gradualmente
    const rolloutInterval = setInterval(async () => {
      const newPercentage = Math.min(
        current + (targetPercentage - current) * 0.1, // 10% por step
        targetPercentage
      );

      await this.updateFlag(flagName, {
        rolloutPercentage: Math.round(newPercentage)
      });

      if (newPercentage >= targetPercentage) {
        clearInterval(rolloutInterval);
      }
    }, interval);

    return {
      status: 'rolling_out',
      flagName,
      from: current,
      to: targetPercentage,
      duration,
      intervalId: rolloutInterval
    };
  },

  /**
   * Buscar histórico de alterações de flag
   * TODO: Implementar quando tabela de auditoria existir
   */
  async getFlagHistory(flagName: FeatureFlagName) {
    // Placeholder para implementação futura
    return [];
  }
};
